import { orchestrationPathResources } from "./Pages/orchestrationPaths";
import { RoutingRulesPaths } from "./Pages/taskRouting/Pages/paths";

/**
 * Orchestration metadata source of truth.
 *
 * Current behavior:
 * - Core sub-nav/page title and description maps are aligned with Spotlight matching.
 * - `_PATCH` values are temporary header/sub-nav copy overrides where integration still differs.
 *
 * Direction:
 * - Unify all metadata with product guidance so Spotlight, sub-nav, and page headers share one canonical copy set.
 */

export const ORCHESTRATION_SUB_NAV_TITLES = {
    [orchestrationPathResources.journeys]: "Journeys",
    [orchestrationPathResources.campaigns]: "Campaigns",
    [orchestrationPathResources.ivrs]: "IVRs",
    [orchestrationPathResources.routing]: "Task Routing",
    [orchestrationPathResources.customFunctions]: "Regal Functions",
    [RoutingRulesPaths.rules]: "Routing Rules",
    [RoutingRulesPaths.queues]: "Task Queues",
    [RoutingRulesPaths.attributes]: "Task Attributes",
} as const;

export const ORCHESTRATION_SUB_PAGE_TITLES = {
    [`${orchestrationPathResources.journeys}/new`]: "Create Journey",
    [`${orchestrationPathResources.campaigns}/new`]: "Create Campaign",
    [`${orchestrationPathResources.ivrs}/new`]: "Create IVR",
    [`${RoutingRulesPaths.queues}/new`]: "Create Queue",
} as const;

// Spotlight-only copy. Inside the Task Routing nav "Task Queues" is unambiguous, but in global
// search it competes with the Live Ops queue metrics page, so Spotlight spells out the difference.
export const ORCHESTRATION_SPOTLIGHT_TITLES = {
    [RoutingRulesPaths.queues]: "Queue Configuration",
} as const;

export const ORCHESTRATION_PAGE_DESCRIPTIONS = {
    [orchestrationPathResources.journeys]:
        "Customer journey orchestration platform for designing interaction workflows.",
    [`${orchestrationPathResources.journeys}/new`]:
        "Visual builder for creating and modifying customer journey workflows.",
    [orchestrationPathResources.campaigns]: "Marketing campaign management system for multi-channel communications.",
    [`${orchestrationPathResources.campaigns}/new`]: "Create a new marketing campaign.",
    [orchestrationPathResources.ivrs]: "Interactive Voice Response system management.",
    [`${orchestrationPathResources.ivrs}/new`]: "Create a new IVR.",
    [orchestrationPathResources.routing]: "Configure call routing and queue management settings.",
    [orchestrationPathResources.customFunctions]: "Create and manage Regal functions for your orchestration workflows.",
    [RoutingRulesPaths.rules]: "Call routing and queue management rules configuration.",
    [RoutingRulesPaths.queues]: "Create and manage queues.",
    [`${RoutingRulesPaths.queues}/new`]: "Create a new queue.",
    [RoutingRulesPaths.attributes]:
        "Create and manage task attributes to be appended to all tasks. Task attributes can be referenced in routing rules for task routing.",
} as const;

// Header/sub-nav title overrides used where page labels differ from base route metadata.
export const ORCHESTRATION_SUB_PAGE_TITLES_PATCH = {
    [orchestrationPathResources.ivrs]: "IVR",
    [`${orchestrationPathResources.routing}/${RoutingRulesPaths.queues}`]: "Queues",
    [`${orchestrationPathResources.routing}/${RoutingRulesPaths.queues}/new`]: "Set up New Queue",
} as const;

// Description/subtitle overrides used where explanatory copy differs from base metadata.
export const ORCHESTRATION_PAGE_DESCRIPTIONS_PATCH = {
    [`${orchestrationPathResources.routing}/${RoutingRulesPaths.rules}`]: "Configure routing rules by task types.",
    [orchestrationPathResources.ivrs]:
        "Create and manage your Interactive Voice Response (IVR) for inbound call routing.",
} as const;
